import React from "react";

import {
	Grid,
	Col,
	Text,
	Icon,
	Button,
	StarRatingWithLogo,
} from "@website-builder/ui/shared/elements";
import {
	handleCTAClick,
	formatColor,
} from "@website-builder/utilities/utils/DesignUtils.js";
import { isBusinessVerticalB2U } from "@website-builder/utilities/utils/helpers.js";
import { DESKTOP_L_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";

import { useMediaQuery } from "@react-hook/media-query";

import {
	FeatureCardsWrapper,
	CardWrapper,
	CardsWrapper,
	IconWrapper,
} from "./styles";

const isB2U = isBusinessVerticalB2U();
const FeatureCards = ({
	label,
	headline,
	description,
	buttonAboveCards,
	cards,
	buttonBelowCards,
	isDarkBg,
	pageSection,
	context,
	starRatingWithLogo,
}) => {
	const isWebScreenBelow1400 = useMediaQuery(`(min-width: ${DESKTOP_L_BP}px)`);

	// Renders Top of the card either Number or Icon
	const hasHeadingContent = label || headline || description !== "<p></p>";

	const renderCardTopContainer = (icon, cardNo) => {
		switch (cards?.showNumber) {
			case true:
				return (
					<Text
						aria-hidden="true"
						variant="headline_0"
						tag="p"
						style={cards?.numberColor ? { color: cards?.numberColor } : null}
						color={{
							base: "secondary",
							color: "blue",
							shade: "300",
						}}
					>
						{cardNo <= 9 ? `0${cardNo}` : cardNo}
					</Text>
				);
			case false:
				return (
					<IconWrapper aria-hidden="true">
						<Icon
							iconName={icon?.iconName}
							size={isB2U ? `${icon?.size}px` : icon?.size}
							stroke={formatColor(icon?.stroke)}
							fill={formatColor(icon?.fill)}
						/>
					</IconWrapper>
				);
		}
	};

	const renderCards = () => {
		return (
			<CardsWrapper>
				{cards?.cardItems.map((cardItem, cardNo) => {
					return (
						<CardWrapper
							hasHeadingContent={hasHeadingContent}
							key={cardNo}
							cardsLength={cards?.cardItems?.length}
							hasBoxShadow={cards?.hasBoxShadow}
							hasNumber={cards?.showNumber}
						>
							{renderCardTopContainer(cardItem?.icon, cardNo + 1)}
							<div className="card-text-content-wrapper">
								{cardItem?.heading ? (
									<Text
										variant="headline_6"
										tag="div"
										color={{
											base: "primary",
											color: "neutral-nightshade",
											shade: "800",
										}}
									>
										{cardItem?.heading}
									</Text>
								) : null}

								{cardItem?.description ? (
									<Text
										variant="paragraph_S"
										color={{
											base: "primary",
											color: "neutral-nightshade",
											shade: "600",
										}}
										tag="div"
										dangerouslySetInnerHTML={{ __html: cardItem?.description }}
									/>
								) : null}
							</div>
						</CardWrapper>
					);
				})}
			</CardsWrapper>
		);
	};

	const renderButton = (cta, additionalClassname) => {
		return cta ? (
			<Col start={1} span={12} mdSpan={6} vAlign="center">
				<Button
					{...cta}
					className={`feature-cards-btn ${additionalClassname}`}
					onClick={() =>
						handleCTAClick(
							cta,
							{
								page_section: pageSection,
								redirect_to: cta?.link?.link,
								target: cta?.link?.target,
								click_text: cta?.buttonText,
							},
							{
								context,
							},
						)
					}
				/>
			</Col>
		) : null;
	};

	// defines card has heading content
	return (
		<FeatureCardsWrapper
			isDarkBg={isDarkBg}
			hasNumber={cards?.showNumber}
			hasHeadingContent={hasHeadingContent}
			hasButtonAboveCards={!!buttonAboveCards}
			className="feature-cards"
		>
			<Grid>
				{hasHeadingContent ? (
					<Col start={4} span={6} mdSpan={6}>
						<div className="feature-card-heading-wrapper flex-column">
							{label ? (
								<Text
									variant="label_M"
									style={{ color: label?.fontColor?.color }}
								>
									{label?.text}
								</Text>
							) : null}

							{starRatingWithLogo &&
								Object.keys(starRatingWithLogo).length > 0 && (
									<div className="feature-card-star-rating">
										<StarRatingWithLogo
											starRatingWithLogo={starRatingWithLogo}
											hasDarkBackground={isDarkBg}
										></StarRatingWithLogo>
									</div>
								)}

							{headline ? (
								<Text
									variant="headline_3"
									tag="h2"
									color={
										isDarkBg
											? {
													base: "primary",
													color: "neutral",
													shade: "white",
												}
											: {
													base: "primary",
													color: "neutral-nightshade",
													shade: "800",
												}
									}
								>
									{headline}
								</Text>
							) : null}
							{description !== "<p></p>" ? (
								<Text
									tag="div"
									variant="paragraph_L"
									color={
										isDarkBg
											? {
													base: "primary",
													color: "neutral",
													shade: "white",
												}
											: {
													base: "primary",
													color: "neutral-nightshade",
													shade: "600",
												}
									}
									dangerouslySetInnerHTML={{ __html: description }}
								/>
							) : null}
						</div>
					</Col>
				) : null}
				{renderButton(buttonAboveCards, "btn-margin-bottom")}
				<Col
					start={cards?.showNumber ? 1 : isWebScreenBelow1400 ? 2 : 1}
					span={cards?.showNumber ? 12 : isWebScreenBelow1400 ? 10 : 12}
					mdSpan={6}
					vAlign="center"
				>
					{renderCards()}
				</Col>
				{renderButton(buttonBelowCards, "btn-margin-top")}
			</Grid>
		</FeatureCardsWrapper>
	);
};

export default FeatureCards;
